import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookOpen, Lightbulb } from "lucide-react";

const GRADES = ["all", "elementary", "middle", "high"];

const HIGHLIGHT = {
  amber: "bg-amber-200/70 hover:bg-amber-300/80 decoration-amber-500",
  indigo: "bg-indigo-200/70 hover:bg-indigo-300/80 decoration-indigo-500",
  rose: "bg-rose-200/70 hover:bg-rose-300/80 decoration-rose-500",
  emerald: "bg-emerald-200/70 hover:bg-emerald-300/80 decoration-emerald-500",
};
const PILL = {
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  indigo: "bg-indigo-100 text-indigo-800 border-indigo-200",
  rose: "bg-rose-100 text-rose-800 border-rose-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

// Render the model essay with clickable annotation highlights.
function AnnotatedModel({ model, annotations, exampleId, activeId, onPick }) {
  if (!annotations?.length) {
    return <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-slate-800 leading-relaxed">{model}</div>;
  }
  // Build segments by walking through model text, splitting on each annotation quote in order.
  let remaining = model;
  const segments = [];
  annotations.forEach((a, i) => {
    const idx = remaining.indexOf(a.quote);
    if (idx === -1) return;
    if (idx > 0) segments.push({ kind: "text", text: remaining.slice(0, idx) });
    segments.push({ kind: "ann", ann: a, i });
    remaining = remaining.slice(idx + a.quote.length);
  });
  if (remaining) segments.push({ kind: "text", text: remaining });

  return (
    <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 sm:p-5 text-slate-800 leading-relaxed">
      {segments.map((s, idx) => {
        if (s.kind === "text") return <span key={idx}>{s.text}</span>;
        const a = s.ann;
        const tid = `${exampleId}-ann-${s.i}`;
        const active = activeId === tid;
        return (
          <button
            key={idx}
            data-testid={`annotation-${tid}`}
            onClick={() => onPick(active ? null : { tid, ...a })}
            className={`px-1 rounded-md cursor-pointer underline decoration-2 underline-offset-4 transition-colors ${HIGHLIGHT[a.color] || HIGHLIGHT.indigo} ${active ? 'ring-2 ring-offset-2 ring-slate-400' : ''}`}
          >
            {a.quote}
          </button>
        );
      })}
    </div>
  );
}

export default function Examples() {
  const { user } = useAuth();
  const [grade, setGrade] = useState(user?.grade_level || "all");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [activeAnn, setActiveAnn] = useState(null); // {tid, label, color, comment}

  useEffect(() => {
    const url = grade === "all" ? "/examples" : `/examples?grade_level=${grade}`;
    api.get(url).then(r => setItems(r.data.examples || []));
  }, [grade]);

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-2xl bg-sky-100 text-sky-700 grid place-items-center"><BookOpen size={20}/></div>
          <h1 className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>Examples Library</h1>
        </div>
        <p className="text-slate-600">Read model writing — then tap any highlight to see WHY it works.</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {GRADES.map(g => (
          <button key={g} data-testid={`examples-filter-${g}`} onClick={()=>{setGrade(g); setOpen(null); setActiveAnn(null);}}
            className={`rounded-full px-4 py-2 text-sm font-bold border-2 capitalize ${grade===g ? 'bg-sky-500 border-sky-600 text-white':'bg-white border-slate-200 text-slate-700 hover:border-sky-300'}`}>
            {g}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {items.map(ex => {
          const isOpen = open === ex.id;
          return (
            <div key={ex.id} data-testid={`example-${ex.id}`} className="tactile-card p-6">
              <div className="flex items-center justify-between mb-3">
                <span className="rounded-full bg-sky-100 text-sky-800 px-3 py-1 text-xs font-bold uppercase tracking-wider">{ex.type}</span>
                {ex.annotations?.length > 0 && (
                  <span className="rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
                    <Lightbulb size={12}/> {ex.annotations.length} insights
                  </span>
                )}
              </div>
              <h3 className="text-xl font-bold mb-2" style={{fontFamily:'Outfit'}}>{ex.title}</h3>
              <p className="text-sm text-slate-500 italic mb-4">"{ex.prompt}"</p>
              <button onClick={()=>{setOpen(isOpen?null:ex.id); setActiveAnn(null);}} data-testid={`example-toggle-${ex.id}`}
                className="text-sm font-bold text-sky-700">{isOpen ? 'Hide model' : 'Show annotated model →'}</button>
              {isOpen && (
                <div className="mt-4 space-y-3 fade-up">
                  <AnnotatedModel
                    model={ex.model}
                    annotations={ex.annotations}
                    exampleId={ex.id}
                    activeId={activeAnn?.tid}
                    onPick={setActiveAnn}
                  />
                  {activeAnn && activeAnn.tid?.startsWith(ex.id) && (
                    <div data-testid={`ann-detail-${activeAnn.tid}`}
                      className={`rounded-2xl border-2 p-4 ${PILL[activeAnn.color] || PILL.indigo}`}>
                      <div className="text-xs uppercase font-bold tracking-widest mb-1">{activeAnn.label}</div>
                      <p className="text-slate-800">{activeAnn.comment}</p>
                    </div>
                  )}
                  {!activeAnn && (
                    <p className="text-xs text-slate-500 italic">Tip: click any highlighted phrase above to see what makes it work.</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
